import { useState } from 'react'
import { Plus, Pencil, Trash2, MessageCircle } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { WizardData, WizardIntent } from './types'

interface StepIntentsProps {
  data: WizardData
  onChange: (data: WizardData) => void
}

interface IntentForm {
  intent_id: string
  name: string
  description: string
  workflow: string
  priority: number
  examples: string
  indicators: string
  is_default: boolean
  require_address_verification: boolean
}

const emptyForm: IntentForm = {
  intent_id: '',
  name: '',
  description: '',
  workflow: 'report_issue',
  priority: 1,
  examples: '',
  indicators: '',
  is_default: false,
  require_address_verification: false,
}

const WORKFLOW_COLORS: Record<string, string> = {
  report_issue: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  check_status: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  rag_fallback: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
}

export function StepIntents({ data, onChange }: StepIntentsProps) {
  const [showForm, setShowForm] = useState(false)
  const [editIdx, setEditIdx] = useState<number | null>(null)
  const [form, setForm] = useState<IntentForm>(emptyForm)

  const hasDepts = data.departments.length > 0

  const startAdd = () => {
    setForm(emptyForm)
    setEditIdx(null)
    setShowForm(true)
  }

  const startEdit = (idx: number) => {
    const intent = data.intents[idx]
    setForm({
      intent_id: intent.intent_id,
      name: intent.name,
      description: intent.description,
      workflow: intent.workflow,
      priority: intent.priority,
      examples: intent.examples.join('\n'),
      indicators: intent.indicators.join(', '),
      is_default: intent.is_default,
      require_address_verification: intent.require_address_verification,
    })
    setEditIdx(idx)
    setShowForm(true)
  }

  const save = () => {
    if (!form.name.trim()) return
    const intent: WizardIntent = {
      ...(editIdx !== null ? data.intents[editIdx] : {}),
      intent_id: form.intent_id || form.name.toLowerCase().replace(/\s+/g, '_'),
      name: form.name,
      description: form.description,
      workflow: form.workflow,
      priority: form.priority,
      examples: form.examples
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
      indicators: form.indicators
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      is_default: form.is_default,
      require_address_verification: form.require_address_verification,
    }

    const intents = [...data.intents]
    if (editIdx !== null) {
      intents[editIdx] = intent
    } else {
      intents.push(intent)
    }
    onChange({ ...data, intents })
    setShowForm(false)
  }

  const remove = (idx: number) => {
    onChange({ ...data, intents: data.intents.filter((_, i) => i !== idx) })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold">Intents</h3>
          <p className="text-sm text-muted-foreground">
            Define how user messages are classified and routed to departments.
          </p>
        </div>
        {data.intents.length > 0 && !showForm && (
          <Button size="sm" onClick={startAdd}>
            <Plus className="h-4 w-4 mr-1" /> Add Intent
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="border-primary/50 bg-primary/5">
          <CardContent className="pt-6 space-y-4">
            <p className="text-sm font-semibold">
              {editIdx !== null ? 'Edit' : 'New'} Intent
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Intent Name</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Report Maintenance Issue"
                />
              </div>
              <div className="space-y-2">
                <Label>Intent ID</Label>
                <Input
                  value={form.intent_id}
                  onChange={(e) => setForm({ ...form, intent_id: e.target.value })}
                  placeholder="report_issue"
                  disabled={editIdx !== null}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="When a user wants to report a problem or request a service"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Workflow</Label>
                <Select value={form.workflow} onValueChange={(v) => setForm({ ...form, workflow: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="report_issue">Report Issue</SelectItem>
                    <SelectItem value="check_status">Check Status</SelectItem>
                    <SelectItem value="rag_fallback">RAG Fallback</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Input
                  type="number"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value) || 1 })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Example Phrases</Label>
              <Textarea
                rows={4}
                value={form.examples}
                onChange={(e) => setForm({ ...form, examples: e.target.value })}
                placeholder={"There is a pothole on my street\nI need to report a water leak\nStreet light is out"}
              />
              <p className="text-xs text-muted-foreground">
                One example per line — more examples improve classification accuracy
              </p>
            </div>
            <div className="space-y-2">
              <Label>Indicators (comma separated)</Label>
              <Input
                value={form.indicators}
                onChange={(e) => setForm({ ...form, indicators: e.target.value })}
                placeholder="report, broken, damaged, leak, pothole"
              />
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.is_default}
                  onCheckedChange={(v) => setForm({ ...form, is_default: v })}
                />
                <Label>Default Intent</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.require_address_verification}
                  onCheckedChange={(v) => setForm({ ...form, require_address_verification: v })}
                />
                <Label>Address Verification</Label>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={save} disabled={!form.name.trim()}>
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {data.intents.length === 0 && !showForm ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
            <MessageCircle className="h-10 w-10 text-muted-foreground" />
            <p className="text-sm font-medium">No intents defined</p>
            <p className="text-xs text-muted-foreground text-center max-w-xs">
              {hasDepts
                ? 'Intents classify incoming messages and route them to the right department.'
                : 'Add departments first (Step 2), then define intents to route messages.'}
            </p>
            <Button size="sm" onClick={startAdd} disabled={!hasDepts}>
              <Plus className="h-4 w-4 mr-1" /> Add First Intent
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {data.intents.map((intent, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{intent.name}</span>
                      <Badge variant="outline" className="font-mono text-xs">
                        {intent.intent_id}
                      </Badge>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${WORKFLOW_COLORS[intent.workflow] || ''}`}
                      >
                        {intent.workflow}
                      </span>
                      {intent.is_default && <Badge variant="secondary">Default</Badge>}
                      {intent.require_address_verification && (
                        <Badge variant="outline">Address Verification</Badge>
                      )}
                    </div>
                    {intent.description && (
                      <p className="text-sm text-muted-foreground mt-1">{intent.description}</p>
                    )}
                    {intent.examples.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {intent.examples.slice(0, 3).map((ex, j) => (
                          <span key={j} className="text-xs bg-muted px-2 py-0.5 rounded">
                            "{ex}"
                          </span>
                        ))}
                        {intent.examples.length > 3 && (
                          <span className="text-xs text-muted-foreground">
                            +{intent.examples.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <Button variant="ghost" size="icon" onClick={() => startEdit(i)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(i)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
